import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import routes from '@/routes';
import { TRIAL_DAYS } from '@/config';
import { useTrialDiscount } from '@/hooks/user';
import { formatPrice } from '@/utils';
import { useMedia } from '@/hooks/responsive';
import { Box, Typography } from '@mui/material';

import {
	PadlockIcon,
	DeleteIcon,
	VerifiedIcon,
	CurrencyExchange
} from './Icons';

function TosLink({ children, to }) {
	return (
		<Link style={{ textDecoration: 'underline' }} to={to} target="_blank">
			{children}
		</Link>
	);
}

function FeatureItem({ icon, title }) {
	const mdDown = useMedia('mdDown');

	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
			{icon}

			<Typography
				variant={mdDown ? 'body0' : 'body2'}
				color="text.secondary"
				sx={{
					maxWidth: '33vw'
				}}
			>
				{title}
			</Typography>
		</Box>
	);
}

const iconsSx = {
	color: 'primary.main',
	width: { xs: '16px', md: '20px' },
	height: { xs: '16px', md: '20px' },
	flexShrink: 0
};

function ExtendedTermsDisclaimer({
	isTrialPlan = false,
	monthlyPlan,
	trialPlan,
	buttonLabel
}) {
	const { t } = useTranslation();
	const trialDiscount = useTrialDiscount();

	const monthlyPrice = formatPrice({
		amount: monthlyPlan?.baseAmount,
		currency: monthlyPlan?.currency
	});

	const disclaimerText = isTrialPlan ? (
		<Trans
			i18nKey="fastCheckout.extendedTerms"
			values={{
				monthlyPrice,
				trialDays: TRIAL_DAYS,
				trialPrice: formatPrice({
					amount: trialDiscount ? 0 : trialPlan?.baseAmount,
					currency: trialPlan?.currency
				}),
				buttonLabel
			}}
			components={{
				anchor1: <TosLink to={routes.terms} />,
				anchor2: <TosLink to={routes.privacyPolicy} />,
				anchor3: <TosLink to={routes.settings} />
			}}
		/>
	) : (
		t('checkout.terms.monthly', { monthlyPrice })
	);

	const features = [
		{
			icon: <PadlockIcon sx={iconsSx} />,
			title: t('fastCheckout.features.security')
		},
		{
			icon: <DeleteIcon sx={iconsSx} />,
			title: t('fastCheckout.features.autodelete')
		},
		{
			icon: <VerifiedIcon sx={iconsSx} />,
			title: 'SOC 2'
		}
	];

	return (
		<Box mt="8px">
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '8px',
					borderBottom: '1px solid #DEDEDF',
					pb: '16px',
					color: 'black'
				}}
			>
				<CurrencyExchange />
				<Typography variant="body2" fontWeight={700} color="black">
					{t('fastCheckout.guarantee')}
				</Typography>
			</Box>
			<Box
				sx={{
					mt: '16px',
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'center',
					gap: { xs: '8px', md: '20px' }
				}}
			>
				{features.map((item, idx) => (
					<FeatureItem key={`FeatureItem--${idx}`} {...item} />
				))}
			</Box>
			<Box
				sx={{
					border: '1px solid #DEDEDF',
					borderRadius: '8px',
					padding: { xs: '16px', md: '20px' },
					gap: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					mt: '24px'
				}}
			>
				<Typography variant="body1" color="text.secondary">
					{disclaimerText}
				</Typography>
			</Box>
		</Box>
	);
}

export default ExtendedTermsDisclaimer;
